import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  type Action,
  Alert,
  Badge,
  Button,
  Card,
  DateField,
  Filters,
  Modal,
  ModalActions,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  SearchSelect,
  Select,
  Table,
} from '@evogard/ui';
import { businessDate, loadFailureMessage } from '@evogard/domain';
import { CONSULTANT_PROFILES } from '@evogard/contract';
import type {
  CatalogVolunteer,
  Consultant,
  VolunteerLink,
  WireConsultantProfile,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { PROFILE_LABEL } from '../../lib/consultant-profile.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';
import { MirrorStamp } from './common.js';

/**
 * Os voluntários da praça (30/10/2026) — o cadastro do ERP, com quantos
 * veículos e quantos boletos cada um tem.
 *
 * A lista morava no Painel de Gestão do comercial, que saiu em 28/10; a rota
 * (`POST /erp/volunteers/search`) ficou sem tela nenhuma. Aqui ela volta para
 * o lugar das outras listas do cadastro, sem as ações de premiação, que são do
 * comercial e moram no Início dele.
 *
 * Voluntário é a pessoa do FORNECEDOR, e não o consultor da casa: o vínculo
 * com uma pessoa da plataforma é `app.sales_consultants`, cadastrado em
 * Comercial › Consultores e times. O selo "Sem vínculo" e o filtro mostram o
 * encontro dos dois cadastros — e os dois exigem `sales.teams:ver`, porque
 * sem ela o recorte revelaria pela contagem quem é consultor da casa.
 *
 * A linha sem vínculo ganha a ação "Vincular a um usuário" (30/10/2026, a
 * pedido do dono): o mesmo `POST /sales/consultants` de Consultores e times,
 * sob a mesma `sales.teams:editar`, com o código do voluntário já preenchido.
 *
 * Os números de veículos e boletos são contagem e nada mais: a tela não soma,
 * não ordena e não compara pessoas por eles — o de boletos é piso enquanto o
 * espelho de boletos estiver incompleto. Ver `catalogVolunteerResultSchema`.
 */
export function VolunteersScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtro na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/voluntarios' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const { page, perPage } = list;

  const canSeeLink = can(context, 'sales.teams', 'ver');
  // @valor
  const link: VolunteerLink | undefined =
    canSeeLink && (search.link === 'vinculados' || search.link === 'sem_vinculo')
      ? search.link
      : undefined;

  const query = useQuery({
    queryKey: ['erp', 'voluntarios', tenantId, term, link, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchVolunteers({
        /* menos de duas letras não vai: o `min(2)` do contrato recusaria enquanto se digita */
        body: { term: term.length >= 2 ? term : undefined, link, page, perPage },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os voluntários'));
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  /*
   * O cadastro de consultores, para o selo e para a ação. Arquivado fica de
   * fora e inativo entra — a mesma régua do filtro no servidor (`link`).
   */
  const consultants = useQuery({
    queryKey: ['comercial', 'consultores', tenantId, true],
    enabled: canSeeLink,
    queryFn: async () => {
      const r = await api.sales.listConsultants({ query: { includeInactive: 'true' } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os consultores desta base'));
      return r.body;
    },
  });
  /* código do voluntário → vínculo; um mapa, e não um `find` por célula */
  const byCode = new Map<string, Consultant>(
    (consultants.data ?? []).map((c) => [c.erpVolunteerCode, c]),
  );
  /*
   * Só se afirma com o cadastro LIDO: sem a permissão, ou com a leitura
   * carregando ou falhando, a tela não sabe, e não marca ninguém — marcar todos
   * diria "ninguém está vinculado" sobre uma porta fechada.
   */
  const isUnlinked = (v: CatalogVolunteer) =>
    consultants.isSuccess && (v.erpCode === null || !byCode.has(v.erpCode));

  /*
   * Vincular é `sales.teams:editar`, a ação da rota. Escolher a pessoa lê a
   * lista de usuários, que é `platform.users:ver` — sem ela a ação aparece
   * desabilitada com o motivo, e não um seletor vazio dizendo que a base não
   * tem ninguém.
   */
  const canLink = canSeeLink && can(context, 'sales.teams', 'editar');
  const canSeePeople = can(context, 'platform.users', 'ver');
  const [linking, setLinking] = useState<CatalogVolunteer | null>(null);

  const linkAction = (v: CatalogVolunteer): Action => {
    const shared = { label: 'Vincular a um usuário', icon: 'pessoa' as const };
    const nothing = () => {};
    if (!canSeePeople)
      return {
        ...shared,
        disabled:
          'Escolher a pessoa exige “Usuários: ver”, na plataforma. Peça a quem administra a plataforma, em Grupos.',
        run: nothing,
      };
    if (consultants.isPending)
      return {
        ...shared,
        disabled: 'O cadastro de consultores ainda está carregando.',
        run: nothing,
      };
    if (consultants.isError)
      return {
        ...shared,
        disabled: `Não foi possível ler o cadastro de consultores desta base: ${consultants.error.message}`,
        run: nothing,
      };
    if (v.erpCode === null)
      return {
        ...shared,
        disabled:
          'Este voluntário não tem código no ERP, e é o código que liga as vendas à pessoa.',
        run: nothing,
      };
    const linked = byCode.get(v.erpCode);
    if (linked !== undefined)
      return {
        ...shared,
        disabled: `Já vinculado a ${linked.user.nome}. Para trocar, edite o vínculo em Comercial › Consultores e times.`,
        run: nothing,
      };
    return { ...shared, run: () => setLinking(v) };
  };

  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result?.total);

  return (
    <Page measure="cheia">
      <PageHeader title="Voluntários">
        Os voluntários ativos de {context?.currentTenant.nome}, como o ERP os entrega.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} voluntário(s)`}
          descricao={<MirrorStamp at={result?.syncedAt ?? null} />}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar voluntários',
              placeholder: 'Nome, código, e-mail ou telefone',
              value: text,
              onChange: setText,
            }}
            toolbar={
              canSeeLink ? (
                <Filters
                  values={{ link: link ?? '' }}
                  onApply={(values) =>
                    list.setFilters({ link: String(values.link ?? '') || undefined })
                  }
                  fields={[
                    {
                      type: 'selecao',
                      name: 'link',
                      label: 'Vínculo',
                      placeholder: 'Todos os voluntários',
                      options: [
                        { value: 'vinculados', label: 'Vinculados a um consultor' },
                        { value: 'sem_vinculo', label: 'Sem vínculo' },
                      ],
                      help: 'Vinculado é quem tem consultor nesta base em Comercial › Consultores e times.',
                    },
                  ]}
                />
              ) : undefined
            }
            label="Voluntários do cadastro"
            items={result?.items ?? []}
            loading={query.isLoading}
            rowKey={(v) => v.code}
            empty={
              term.length >= 2
                ? 'Nenhum voluntário com esse termo — ele casa nome, código, e-mail e telefone.'
                : link === 'sem_vinculo'
                  ? 'Todos os voluntários desta base estão vinculados a um consultor.'
                  : link === 'vinculados'
                    ? 'Nenhum voluntário desta base está vinculado a um consultor.'
                    : 'O cadastro de voluntários desta base está vazio.'
            }
            rowTitle={(v) => v.nome ?? `Voluntário ${v.erpCode ?? v.code}`}
            rowSubtitle={(v) => `Código ${v.erpCode ?? v.code} nesta base`}
            rowIcon="cracha"
            rowActions={canLink ? (v): Action[] => [linkAction(v)] : undefined}
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (v) => (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {v.nome ?? <NotProvided>sem nome</NotProvided>}
                    {isUnlinked(v) && <Badge tone="alerta">Sem vínculo</Badge>}
                  </span>
                ),
              },
              {
                /* o do SGA — é o que se dita e o que casa com o vínculo da casa */
                title: 'Código',
                minWidth: '5.5rem',
                cell: (v) => v.erpCode ?? <NotProvided>sem código no ERP</NotProvided>,
              },
              {
                title: 'E-mail',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (v) => v.email ?? <NotProvided>não informado</NotProvided>,
              },
              {
                title: 'Telefone',
                minWidth: '9.5rem',
                cell: (v) => v.telefone ?? <NotProvided>não informado</NotProvided>,
              },
              {
                /* o zero fica escrito: célula vazia pareceria dado que não veio */
                title: 'Veículos',
                alignment: 'fim',
                minWidth: '5.5rem',
                cell: (v) => <span className="tabular-nums">{v.vehicles}</span>,
              },
              {
                title: 'Boletos',
                alignment: 'fim',
                minWidth: '5.5rem',
                cell: (v) => <span className="tabular-nums">{v.boletos}</span>,
              },
            ]}
          />

          {result !== undefined && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="voluntários"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}

      {linking !== null && linking.erpCode !== null && (
        <LinkVolunteerModal
          volunteer={linking}
          erpCode={linking.erpCode}
          linkedUserIds={new Set((consultants.data ?? []).map((c) => c.user.id))}
          toClose={() => setLinking(null)}
        />
      )}
    </Page>
  );
}

/**
 * Vincular o voluntário a uma pessoa da plataforma — o formulário de
 * Comercial › Consultores e times, com o código já escolhido pela linha.
 *
 * A pessoa vem de uma busca NO SERVIDOR: a GO tem mais de 390 usuários, e a
 * lista de uma página só (200) deixaria metade da praça de fora sem avisar.
 * Quem já é consultor nesta base sai das opções — a chave única recusaria, e
 * a recusa chegaria depois de escolher.
 *
 * O e-mail do voluntário, quando casa EXATO com o de um usuário, pré-escolhe
 * a pessoa: os usuários da GO nasceram do cadastro de voluntários, com o mesmo
 * e-mail. É sugestão, e não decisão — o campo continua editável.
 */
function LinkVolunteerModal({
  volunteer,
  erpCode,
  linkedUserIds,
  toClose,
}: {
  volunteer: CatalogVolunteer;
  erpCode: string;
  linkedUserIds: ReadonlySet<string>;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const [userId, setUserId] = useState('');
  const [userLabel, setUserLabel] = useState('');
  const [term, setTerm] = useState('');
  const search = useDebouncedTerm(term);
  /* A régua é obrigatória desde 22/10/2026, e nasce em externo — o fluxo de hoje. */
  const [profile, setProfile] = useState<WireConsultantProfile>('externo');
  const [teamId, setTeamId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(businessDate(new Date()));
  const [error, setError] = useState<string | null>(null);

  const people = useQuery({
    queryKey: ['plataforma', 'usuarios', tenantId, 'busca', search],
    queryFn: async () => {
      const r = await api.platform.listUsers({
        query: { page: 1, perPage: 50, search: search.trim() || undefined },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'as pessoas desta base'));
      return r.body.items;
    },
  });

  const email = volunteer.email?.trim().toLowerCase() ?? '';
  const suggestion = useQuery({
    queryKey: ['plataforma', 'usuarios', tenantId, 'email', email],
    enabled: email.length > 0,
    queryFn: async () => {
      const r = await api.platform.listUsers({
        query: { page: 1, perPage: 5, search: email },
      });
      if (r.status !== 200) return null;
      return (
        r.body.items.find(
          (u) => u.email.toLowerCase() === email && !linkedUserIds.has(u.id),
        ) ?? null
      );
    },
  });
  const suggested = suggestion.data ?? null;
  /* só na chegada da sugestão: quem trocou a pessoa não a vê voltar sozinha */
  useEffect(() => {
    if (suggested === null) return;
    setUserId((current) => current || suggested.id);
    setUserLabel((current) => current || `${suggested.nome} · ${suggested.email}`);
  }, [suggested]);

  const teams = useQuery({
    queryKey: ['comercial', 'times', tenantId, false],
    queryFn: async () => {
      const r = await api.sales.listTeams({ query: {} });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os times desta base'));
      return r.body;
    },
  });

  const options = (people.data ?? [])
    .filter((u) => !linkedUserIds.has(u.id))
    .map((u) => ({ value: u.id, label: `${u.nome} · ${u.email}` }));

  const create = useMutation({
    mutationFn: async () => {
      const r = await api.sales.createConsultant({
        body: {
          userId,
          erpVolunteerCode: erpCode,
          teamId: teamId ? Number(teamId) : null,
          profile,
          effectiveFrom,
        },
      });
      return ok(r, 201, 'Não foi possível vincular a pessoa.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      void client.invalidateQueries({ queryKey: ['erp', 'voluntarios'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) toClose();
      }}
      title="Vincular a um usuário"
      descricao={`${volunteer.nome ?? 'Voluntário'} · código ${erpCode} nesta base. O código casa as vendas importadas com a pessoa.`}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}
        {people.isError && <Alert tone="erro">{people.error.message}</Alert>}

        <SearchSelect
          label="Usuário"
          required
          placeholder="Escolha…"
          searchPlaceholder="Nome ou e-mail"
          emptyMessage="Nenhum usuário sem vínculo com esse nome ou e-mail."
          options={options}
          value={userId}
          selectedLabel={userLabel || undefined}
          loading={people.isFetching}
          onSearchChange={setTerm}
          onChange={(next) => {
            setUserId(next);
            setUserLabel(options.find((o) => o.value === next)?.label ?? '');
          }}
          help={
            suggested !== null && userId === suggested.id
              ? 'Escolhido pelo e-mail do voluntário no ERP.'
              : 'Quem já é consultor nesta base não aparece.'
          }
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Régua"
            required
            value={profile}
            onChange={(e) => setProfile(e.target.value as WireConsultantProfile)}
            help="Sob qual régua a pessoa é paga."
          >
            {CONSULTANT_PROFILES.map((value) => (
              <option key={value} value={value}>
                {PROFILE_LABEL[value]}
              </option>
            ))}
          </Select>

          <Select label="Time" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">Sem time</option>
            {(teams.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </Select>

          {teamId && (
            <DateField
              label="A partir de"
              required
              value={effectiveFrom}
              onChange={setEffectiveFrom}
              help="Desde quando responde ao líder do time."
            />
          )}
        </div>

        <ModalActions>
          <Button type="submit" loading={create.isPending} disabled={!userId}>
            Vincular
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
